import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, UserCheck, UserX, BookOpen } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users", { params: { status: "pending" } }),
        ]);
        setStats(s.data);
        setRecent(u.data.slice(0, 5));
      } catch (e) { setError(errorMessage(e)); }
    })();
  }, []);

  const cards = [
    { label: "Pending", value: stats?.pending ?? "-", icon: UserCheck, tone: "text-primary", to: "/admin/pending" },
    { label: "Active", value: stats?.active ?? "-", icon: Users, tone: "text-foreground", to: "/admin/users" },
    { label: "Rejected", value: stats?.rejected ?? "-", icon: UserX, tone: "text-destructive", to: "/admin/users" },
    { label: "Modules", value: stats?.modules ?? "-", icon: BookOpen, tone: "text-foreground", to: "/admin/modules" },
  ];

  return (
    <AppShell section="admin">
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="overline mb-3">Admin Console</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-8">Overview</h1>

        {error && <div className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">{error}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 border border-border bg-white">
          {cards.map((c, i) => (
            <Link key={c.label} to={c.to} className={`p-6 card-lift ${i < 3 ? "border-r border-border" : ""} ${i < 2 ? "border-b lg:border-b-0 border-border" : ""}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline">{c.label}</div>
                <c.icon className={`h-4 w-4 ${c.tone}`} aria-hidden="true" />
              </div>
              <div className={`font-display font-black text-4xl tracking-tight ${c.tone}`}>{c.value}</div>
            </Link>
          ))}
        </div>

        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-display font-bold text-xl tracking-tight">Recent pending requests</h2>
            <Link to="/admin/pending" className="text-[13px] font-semibold text-primary link-underline">View all <ArrowRight className="inline h-3.5 w-3.5" /></Link>
          </div>
          <div className="border border-border bg-white">
            {recent.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-[13px]" data-testid="no-pending-empty">No pending registrations.</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-accent/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold">Email</th>
                    <th className="text-left p-3 font-semibold">Organization</th>
                    <th className="text-left p-3 font-semibold">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((u) => (
                    <tr key={u.user_id} className="border-t border-border">
                      <td className="p-3 font-semibold">{u.full_name}</td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3 text-muted-foreground">{u.organization || "-"}</td>
                      <td className="p-3 text-muted-foreground">{u.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
